import classnames from 'classnames';
import {HTMLAttributes, ReactNode, forwardRef} from 'react';

import CloseButton from '@/closeButton';
import {ComponentPlacementDirection, ComponentSizeXSToL} from '@/common/types';
import FontAwesomeV6Icon, {FontAwesomeV6IconProps} from '@/fontAwesomeV6Icon';
import {BodyTwoText, Heading5} from '@/typography';

import moduleStyles from './popover.module.scss';

export interface PopoverProps extends HTMLAttributes<HTMLDivElement> {
  /** Popover title */
  title: string;
  /** Popover direction/position relative to connected element */
  direction?: ComponentPlacementDirection;
  /** Popover icon */
  icon?: FontAwesomeV6IconProps;
  /** Popover image */
  image?: {
    src: string;
    alt: string;
  };
  /** Popover content text*/
  content: string;
  children?: ReactNode;
  /** Popover function to close the popover */
  onClose: () => void;
  /** Custom className */
  className?: string;
  /** Popover custom styles (used for positioning the popover on the go) */
  style?: React.CSSProperties;
  size?: Exclude<ComponentSizeXSToL, 'l' | 's' | 'xs'>;
  /** Popover buttons */
  buttons?: ReactNode;
}

/**
 * ### Production-ready Checklist:
 * * (✔) implementation of component approved by design team;
 * * (✔) has storybook, covered with stories and documentation;
 * * (✔) has tests: test every prop, every state and every interaction that's js related;
 * * (see ./__tests__/Popover.test.tsx, ./__tests__/WithPopover.test.tsx);
 * * (?) passes accessibility checks;
 *
 * ###  Status: ```Ready For Dev```
 *
 * Design System: Popover Component.
 * Can be used to render a Popover.
 */
const Popover = forwardRef<HTMLDivElement, PopoverProps>(
  (
    {
      icon,
      image,
      title,
      content,
      buttons,
      direction = 'onTop',
      onClose,
      className,
      size = 'm',
      style = {},
      children,
      ...HTMLAttributes
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={classnames(
          moduleStyles.popover,
          image && moduleStyles['popover-withImage'],
          moduleStyles[`popover-${direction}`],
          moduleStyles[`popover-${size}`],
          className,
        )}
        style={style}
        {...HTMLAttributes}
      >
        <CloseButton
          className={moduleStyles.closeButton}
          onClick={onClose}
          size="l"
          aria-label="Close"
        />
        {image && (
          <div className={moduleStyles.imageSection}>
            <img src={image.src} alt={image.alt} />
          </div>
        )}

        <div className={moduleStyles.informationalSection}>
          {icon && (
            <div className={moduleStyles.iconSection}>
              <FontAwesomeV6Icon {...icon} />
            </div>
          )}
          <div className={moduleStyles.contentSection}>
            <div className={moduleStyles.textSection}>
              <Heading5>{title}</Heading5>
              <BodyTwoText>{content}</BodyTwoText>
              {children}
            </div>
            {buttons && (
              <div className={moduleStyles.buttonsSection}>{buttons}</div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

export default Popover;
